import { z } from "zod";
import { getPeopleAvatarBucketPath } from "@/lib/utils/avatar";

export const emailValidation = z
  .string()
  .email("Invalid email")
  .min(2, "Too short")
  .max(50, "Too long");

export const passwordValidation = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(64, "Password must be at most 64 characters");
// .refine(
//   val => /[a-z]/.test(val) && /[A-Z]/.test(val) && /\d/.test(val) && /[^a-zA-Z0-9]/.test(val),
//   {
//     message: "Password must include lowercase, uppercase, number, and special character",
//   }
// );

export const firstNameValidation = z.string().min(2, "Too short").max(50, "Too long");
export const lastNameValidation = z.string().min(2, "Too short").max(50, "Too long");

export const urlValidation = z.string().url("Must be a valid URL");

export const companyUrlValidation = z
  .string()
  .trim()
  .min(1, "Company URL is required")
  .refine(
    val => {
      const urlWithProtocol = val.match(/^https?:\/\//) ? val : `https://${val}`;
      try {
        const url = new URL(urlWithProtocol);
        const hostname = url.hostname;
        // Must be a valid domain with exactly one dot (no subdomains)
        const parts = hostname.split(".");
        return parts.length === 2 && parts.every(part => part.length > 0);
      } catch {
        return false;
      }
    },
    {
      message: "Must be a valid domain without subdomains (e.g., acme.com)",
    }
  )
  .transform(val => {
    return val.replace(/^https?:\/\//, "");
  });

export const linkedInUrlPattern =
  /^(https?:\/\/)?(www\.)?([a-z]{2}\.)?linkedin\.com\/in\/[a-zA-Z0-9-_%À-ÿ]+\/?(\?[^#]*)?$/;

export const linkedInUrlValidation = z
  .string()
  .trim()
  .refine(
    val => {
      // Add protocol if missing
      const urlWithProtocol = val.startsWith("http") ? val : `https://${val}`;
      return (
        /^(https?:\/\/)?(www\.)?([a-z]{2}\.)?linkedin\.com\/in\/[a-zA-Z0-9-_%À-ÿ]+\/?(\?[^#]*)?$/.test(
          val
        ) &&
        /^https?:\/\/(www\.)?([a-z]{2}\.)?linkedin\.com\/in\/[a-zA-Z0-9-_%À-ÿ]+\/?(\?[^#]*)?$/.test(
          urlWithProtocol
        )
      );
    },
    {
      message: "Invalid LinkedIn URL",
    }
  );

export const otpValidation = z.string().min(6).max(6);

export const listVisibilityValidation = z.enum(["workspace", "private", "restricted"]);

export const createOrEditListSchema = z.object({
  name: firstNameValidation,
  color: z.string(),
  type: z.enum(["automatic", "manual", "import"]),
  visibility: listVisibilityValidation,
  personaId: z.string().nullable(),
  members: z.array(z.object({ id: z.string(), fullName: z.string() })),
  companies: z.array(z.string()).optional(),
});

export const editCompanyInformationFormSchema = z.object({
  name: firstNameValidation,
  summary: z.string().max(1500, "Must be 1500 characters or less"),
  productBenefits: z.string().max(1500, "Must be 1500 characters or less"),
  differentiatedValue: z.string().max(1500, "Must be 1500 characters or less"),
  painPointsSolved: z.string().max(1500, "Must be 1500 characters or less"),
});

export const editPersonaFormSchema = z.object({
  id: z.string().uuid("Invalid persona ID"),
  name: z.string().min(1, "Name is required").max(255, "Name must be less than 255 characters"),
  jobTitleIds: z
    .array(z.string().uuid("Invalid job title ID"))
    .min(1, "Select at least one job title"),
  summary: z.string().max(1500, "Must be 1500 characters or less"),
  persona_role: z.string().max(1500, "Must be 1500 characters or less"),
  persona_responsibilities: z.string().max(1500, "Must be 1500 characters or less"),
  benefit_of_product: z.string().max(1500, "Must be 1500 characters or less"),
});

// Connector Group Validators
export const connectorGroupNameValidation = z
  .string()
  .min(1, "Name is required")
  .max(255, "Name must be at most 255 characters");

export const connectorGroupDescriptionValidation = z
  .string()
  .max(1000, "Description must be at most 1000 characters")
  .optional();

export const connectorGroupValidation = z.object({
  name: connectorGroupNameValidation,
  description: connectorGroupDescriptionValidation,
  workspaceId: z.string().uuid("Invalid workspace ID"),
  maxOutreachAttempts: z.number().int().min(1).max(100).nullable().optional(),
  outreachPeriodDays: z.number().int().min(1).max(365).default(30),
  requestsPer30Days: z.number().int().min(1).max(1000).optional(),
  introductionApproverIds: z.array(z.string().uuid("Invalid user ID")).optional(),
  ghostWriteAsUserIds: z.array(z.string().uuid("Invalid user ID")).optional(),
  autoDisableAfterLimit: z.boolean().default(true),
});

export const connectorGroupUpdateValidation = z.object({
  name: connectorGroupNameValidation.optional(),
  description: connectorGroupDescriptionValidation,
  maxOutreachAttempts: z.number().int().min(1).max(100).nullable().optional(),
  outreachPeriodDays: z.number().int().min(1).max(365).optional(),
  requestsPer30Days: z.number().int().min(1).max(1000).optional(),
  introductionApproverIds: z.array(z.string().uuid("Invalid user ID")).optional(),
  ghostWriteAsUserIds: z.array(z.string().uuid("Invalid user ID")).optional(),
  autoDisableAfterLimit: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

export const connectorGroupContactsValidation = z.object({
  contactIds: z
    .array(z.string().uuid("Invalid contact ID"))
    .min(1, "At least one contact ID is required"),
});

export const connectorGroupIntroductionUsersValidation = z.object({
  userIds: z.array(z.string().uuid("Invalid user ID")).min(1, "At least one user ID is required"),
});

// Database row validator for connector groups
export const connectorGroupRowValidation = z.object({
  id: z.string().uuid("Invalid connector group ID"),
  name: z.string().min(1, "Name is required"),
  description: z.string().nullable().optional(),
  workspace_id: z.string().uuid("Invalid workspace ID"),
  created_at: z
    .string()
    .refine(val => !isNaN(Date.parse(val)), { message: "Invalid created_at timestamp" }),
  updated_at: z
    .string()
    .refine(val => !isNaN(Date.parse(val)), { message: "Invalid updated_at timestamp" }),
  is_active: z.boolean(),
  max_outreach_attempts: z.number().int().min(1).max(100).nullable().optional(),
  outreach_period_days: z.number().int().min(1).max(365),
  requests_per_30_days: z.number().int().min(1).max(1000).nullable().optional(),
  introduction_approver_ids: z.array(z.string().uuid("Invalid user ID")).nullable().optional(),
  ghost_write_as_user_ids: z.array(z.string().uuid("Invalid user ID")).nullable().optional(),
  auto_disable_after_limit: z.boolean(),
});

// Database row validator for extended network contacts
export const extendedNetworkContactRowValidation = z.object({
  id: z.string().uuid("Invalid contact ID"),
  pdl_id: z.string().min(1, "PDL ID is required"),
  first_name: z.string().nullable().optional(),
  last_name: z.string().nullable().optional(),
  avatar_updated_at: z
    .string()
    .datetime("Invalid avatar_updated_at timestamp")
    .nullable()
    .optional(),
  relationship: z.string().optional(),
  workspace_id: z.string().uuid("Invalid workspace ID"),
  connection_count: z.number().int().min(0).nullable(),
  url: z.string().nullable().optional(), // This is the LinkedIn URL field in the database
  email: z.string().email("Invalid email").optional(),
  connector_group_id: z.string().uuid("Invalid connector group ID").nullable().optional(),
});

// Transform Functions
export function transformConnectorGroup(
  row: Record<string, unknown>
): import("@/types").ConnectorGroup {
  try {
    const validatedRow = connectorGroupRowValidation.parse(row);

    return {
      id: validatedRow.id,
      name: validatedRow.name,
      description: validatedRow.description,
      workspaceId: validatedRow.workspace_id,
      createdAt: validatedRow.created_at,
      updatedAt: validatedRow.updated_at,
      isActive: validatedRow.is_active,
      maxOutreachAttempts: validatedRow.max_outreach_attempts,
      outreachPeriodDays: validatedRow.outreach_period_days,
      introductionApproverIds: validatedRow.introduction_approver_ids,
      ghostWriteAsUserIds: validatedRow.ghost_write_as_user_ids,
      autoDisableAfterLimit: validatedRow.auto_disable_after_limit,
    };
  } catch (error) {
    console.error("Error validating connector group row:", error);
    throw new Error("Invalid connector group data structure");
  }
}

export function transformExtendedNetworkContact(
  row: Record<string, unknown>
): import("@/types").ExtendedNetworkContact {
  try {
    const validatedRow = extendedNetworkContactRowValidation.parse(row);

    return {
      id: validatedRow.id,
      pdlId: validatedRow.pdl_id,
      firstName: validatedRow.first_name || "",
      lastName: validatedRow.last_name || "",
      avatar: validatedRow.avatar_updated_at
        ? getPeopleAvatarBucketPath(validatedRow.pdl_id)
        : null,
      relationship: validatedRow.relationship,
      workspaceId: validatedRow.workspace_id,
      connectionCount: validatedRow.connection_count,
      linkedInUrl: validatedRow.url || "", // Map url field to linkedInUrl
      email: validatedRow.email,
      connectorGroupId: validatedRow.connector_group_id,
    };
  } catch (error) {
    console.error("Error validating extended network contact row:", error);
    throw new Error("Invalid extended network contact data structure");
  }
}

export const referralFormSchema = z.object({
  referrerEmail: emailValidation,
  referrerFirstName: firstNameValidation,
  referrerLastName: lastNameValidation,
  referralCompanyEmail: emailValidation,
  referralCompanyUrl: companyUrlValidation,
  referralCompanyLinkedInUrl: linkedInUrlValidation,
});

export const moreDetailsFormSchema = z.object({
  firstName: firstNameValidation,
  lastName: lastNameValidation,
  referredTitle: z.string().min(1, "Title is required"),
  referredCompanyEmail: emailValidation,
});

export type ReferralFormSchema = z.infer<typeof referralFormSchema>;
export type MoreDetailsFormSchema = z.infer<typeof moreDetailsFormSchema>;
